import { useEffect, useState } from "react";

import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

import PageHeader from "@/components/PageHeader";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import {
  Plus,
  Package,
  MoreVertical,
  Pencil,
  Power,
  Trash2,
  AlertTriangle,
  ReceiptText,
  Check,
  X,
} from "lucide-react";

import { toast } from "sonner";


const brl = (v) =>
  Number(v || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });


const UNITS = [
  "UN",
  "L",
  "KG",
  "M",
  "M²",
  "M³",
  "PC",
  "CX",
  "PAR",
  "PCT",
];


const EMPTY = {
  part_number: "",
  name: "",
  unit_cost: 0,

  quantidade_estoque: 0,
  estoque_minimo: 0,
  estoque_maximo: 0,

  unidade_medida: "UN",
  categoria: "",
};


const EMPTY_REQUEST = {
  part_number: "",
  name: "",

  quantidade_necessaria: 1,

  unidade_medida: "UN",
  categoria: "",

  observacao: "",
};


const EMPTY_QUOTE = {
  supplier_name: "",
  quantity: 1,
  unit_cost: 0,
};


const EMPTY_APPROVAL = {
  unit_cost: 0,

  quantidade_estoque: 0,
  estoque_minimo: 0,
  estoque_maximo: 0,
};


export default function Parts() {
  const { user } = useAuth();

  const isAdmin =
    user?.role === "admin";

  const isGestor =
    user?.role === "gestor";

  const isMecanico =
    user?.role === "operador" &&
    user?.sub_role === "mecanico";

  const canManageParts =
    isAdmin || isGestor;

  const canDeleteParts =
    isAdmin;


  // ================================
  // LISTA
  // ================================

  const [list, setList] = useState([]);


  // ================================
  // CADASTRO ADMIN / GESTOR
  // ================================

  const [open, setOpen] = useState(false);

  const [editing, setEditing] =
    useState(null);

  const [form, setForm] =
    useState(EMPTY);


  // ================================
  // SOLICITAÇÃO DO MECÂNICO
  // ================================

  const [requestOpen, setRequestOpen] =
    useState(false);

  const [requestForm, setRequestForm] =
    useState(EMPTY_REQUEST);


  // ================================
  // COTAÇÃO
  // ================================

  const [quoteOpen, setQuoteOpen] =
    useState(false);

  const [quotePart, setQuotePart] =
    useState(null);

  const [quoteForm, setQuoteForm] =
    useState(EMPTY_QUOTE);


  // ================================
  // APROVAÇÃO
  // ================================

  const [approvalOpen, setApprovalOpen] =
    useState(false);

  const [approvalPart, setApprovalPart] =
    useState(null);

  const [approvalForm, setApprovalForm] =
    useState(EMPTY_APPROVAL);


  // ================================
  // REJEIÇÃO
  // ================================

  const [rejectOpen, setRejectOpen] =
    useState(false);

  const [rejectPart, setRejectPart] =
    useState(null);

  const [rejectReason, setRejectReason] =
    useState("");


  // ================================
  // EXCLUSÃO
  // ================================

  const [pendingDelete, setPendingDelete] =
    useState(null);


  // ================================
  // CARREGAMENTO
  // ================================

  const load = () =>
    api
      .get("/parts")
      .then(({ data }) =>
        setList(data)
      )
      .catch((e) =>
        toast.error(
          formatApiError(
            e.response?.data?.detail
          )
        )
      );


  useEffect(() => {
    load();
  }, []);


  // ================================
  // NOVA PEÇA ADMIN/GESTOR
  // ================================

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };


  const openEdit = (p) => {
    setEditing(p);

    setForm({
      part_number:
        p.part_number || "",

      name:
        p.name || "",

      unit_cost:
        p.unit_cost || 0,

      quantidade_estoque:
        p.quantidade_estoque || 0,

      estoque_minimo:
        p.estoque_minimo || 0,

      estoque_maximo:
        p.estoque_maximo || 0,

      unidade_medida:
        p.unidade_medida || "UN",

      categoria:
        p.categoria || "",
    });

    setOpen(true);
  };


  const save = async () => {
    if (
      !form.part_number ||
      !form.name
    ) {
      return toast.error(
        "Código e Nome são obrigatórios"
      );
    }

    const payload = {
      ...form,

      unit_cost:
        Number(form.unit_cost) || 0,

      quantidade_estoque:
        Number(
          form.quantidade_estoque
        ) || 0,

      estoque_minimo:
        Number(
          form.estoque_minimo
        ) || 0,

      estoque_maximo:
        Number(
          form.estoque_maximo
        ) || 0,
    };


    try {
      if (editing) {
        await api.put(
          `/parts/${editing.id}`,
          {
            ...payload,

            active:
              editing.active !== false,
          }
        );

        toast.success(
          "Peça atualizada"
        );
      } else {
        await api.post(
          "/parts",
          payload
        );

        toast.success(
          "Peça cadastrada"
        );
      }


      setOpen(false);
      setForm(EMPTY);
      setEditing(null);

      load();

    } catch (e) {
      toast.error(
        formatApiError(
          e.response?.data?.detail
        )
      );
    }
  };


  // ================================
  // SOLICITAÇÃO MECÂNICO
  // ================================

  const requestPart = async () => {
    if (!requestForm.name.trim()) {
      return toast.error(
        "Informe o nome da peça"
      );
    }


    if (
      Number(
        requestForm.quantidade_necessaria
      ) <= 0
    ) {
      return toast.error(
        "Informe uma quantidade válida"
      );
    }


    try {
      await api.post(
        "/parts/request",
        {
          ...requestForm,

          quantidade_necessaria:
            Number(
              requestForm.quantidade_necessaria
            ) || 1,
        }
      );


      toast.success(
        "Solicitação enviada para aprovação"
      );


      setRequestOpen(false);
      setRequestForm(
        EMPTY_REQUEST
      );

      load();

    } catch (e) {
      toast.error(
        formatApiError(
          e.response?.data?.detail
        )
      );
    }
  };


  // ================================
  // COTAÇÃO
  // ================================

  const openQuote = (part) => {
    setQuotePart(part);

    setQuoteForm({
      supplier_name: "",
      quantity:
        part.quantidade_necessaria || 1,
      unit_cost: 0,
    });

    setQuoteOpen(true);
  };


  const saveQuote = async () => {
    if (!quotePart) return;


    if (
      !quoteForm.supplier_name.trim()
    ) {
      return toast.error(
        "Informe o fornecedor"
      );
    }


    if (
      Number(quoteForm.unit_cost) <= 0
    ) {
      return toast.error(
        "Informe o valor unitário"
      );
    }


    try {
      await api.post(
        `/parts/${quotePart.id}/quotes`,
        {
          supplier_name:
            quoteForm.supplier_name,

          quantity:
            Number(
              quoteForm.quantity
            ) || 1,

          unit_cost:
            Number(
              quoteForm.unit_cost
            ) || 0,
        }
      );


      toast.success(
        "Cotação adicionada"
      );


      setQuoteOpen(false);
      setQuotePart(null);

      setQuoteForm(
        EMPTY_QUOTE
      );

      load();

    } catch (e) {
      toast.error(
        formatApiError(
          e.response?.data?.detail
        )
      );
    }
  };


  // ================================
  // APROVAÇÃO
  // ================================

  const openApproval = (part) => {
    setApprovalPart(part);

    setApprovalForm({
      unit_cost:
        part.unit_cost || 0,

      quantidade_estoque:
        0,

      estoque_minimo:
        0,

      estoque_maximo:
        0,
    });

    setApprovalOpen(true);
  };


  const approvePart = async () => {
    if (!approvalPart) return;


    try {
      await api.post(
        `/parts/${approvalPart.id}/approve`,
        {
          unit_cost:
            Number(
              approvalForm.unit_cost
            ) || 0,

          quantidade_estoque:
            Number(
              approvalForm.quantidade_estoque
            ) || 0,

          estoque_minimo:
            Number(
              approvalForm.estoque_minimo
            ) || 0,

          estoque_maximo:
            Number(
              approvalForm.estoque_maximo
            ) || 0,
        }
      );


      toast.success(
        "Peça aprovada"
      );


      setApprovalOpen(false);
      setApprovalPart(null);

      setApprovalForm(
        EMPTY_APPROVAL
      );

      load();

    } catch (e) {
      toast.error(
        formatApiError(
          e.response?.data?.detail
        )
      );
    }
  };


  // ================================
  // REJEIÇÃO
  // ================================

  const openReject = (part) => {
    setRejectPart(part);
    setRejectReason("");
    setRejectOpen(true);
  };


  const rejectPartRequest = async () => {
    if (!rejectPart) return;


    if (!rejectReason.trim()) {
      return toast.error(
        "Informe o motivo da rejeição"
      );
    }


    try {
      await api.post(
        `/parts/${rejectPart.id}/reject`,
        {
          reason:
            rejectReason.trim(),
        }
      );


      toast.success(
        "Solicitação rejeitada"
      );


      setRejectOpen(false);
      setRejectPart(null);
      setRejectReason("");

      load();

    } catch (e) {
      toast.error(
        formatApiError(
          e.response?.data?.detail
        )
      );
    }
  };


  // ================================
  // ATIVAR / INATIVAR
  // ================================

  const toggleActive = async (p) => {
    try {
      await api.patch(
        `/parts/${p.id}`,
        {
          active:
            p.active === false,
        }
      );


      toast.success(
        p.active === false
          ? "Peça ativada"
          : "Peça inativada"
      );


      load();

    } catch (e) {
      toast.error(
        formatApiError(
          e.response?.data?.detail
        )
      );
    }
  };


  // ================================
  // EXCLUSÃO
  // ================================

  const doDelete = async () => {
    if (!pendingDelete) return;


    try {
      await api.delete(
        `/parts/${pendingDelete.id}`
      );


      toast.success(
        "Peça excluída"
      );


      setPendingDelete(null);

      load();

    } catch (e) {
      toast.error(
        formatApiError(
          e.response?.data?.detail
        )
      );
    }
  };


  // ================================
  // CLASSE DO ESTOQUE
  // ================================

  const stockRowClass = (p) => {
    if (
      p.status_aprovacao ===
      "Pendente"
    ) {
      return "";
    }


    const qty =
      Number(
        p.quantidade_estoque || 0
      );

    const min =
      Number(
        p.estoque_minimo || 0
      );


    if (min <= 0) return "";

    if (qty <= 0) {
      return "bg-destructive/10 hover:bg-destructive/15";
    }

    if (qty <= min) {
      return "bg-yellow-500/10 hover:bg-yellow-500/15";
    }

    return "";
  };


  // ================================
  // STATUS
  // ================================

  const renderStatus = (p) => {
    const status =
      p.status_aprovacao ||
      "Aprovado";


    if (status === "Pendente") {
      return (
        <Badge
          variant="outline"
          className="text-[10px] border-yellow-500/40 text-yellow-600"
        >
          Pendente
        </Badge>
      );
    }


    if (status === "Rejeitado") {
      return (
        <Badge
          variant="outline"
          className="text-[10px] border-destructive/40 text-destructive"
        >
          Rejeitado
        </Badge>
      );
    }


    if (p.active === false) {
      return (
        <Badge
          variant="outline"
          className="text-[10px] text-muted-foreground"
        >
          Inativo
        </Badge>
      );
    }


    return (
      <Badge
        variant="outline"
        className="text-[10px] bg-primary/10 border-primary/30 text-primary"
      >
        Aprovado
      </Badge>
    );
  };


  return (
    <div
      className="p-6 max-w-7xl mx-auto"
      data-testid="parts-page"
    >

      {/* ============================
          CABEÇALHO
      ============================ */}

      <PageHeader
        title="Peças"
        subtitle="Catálogo de peças, solicitações e controle de estoque"
        actions={
          <div className="flex gap-2">

            {canManageParts && (
              <Button
                className="gap-2"
                data-testid="new-part-btn"
                onClick={openNew}
              >
                <Plus className="w-4 h-4" />

                Nova Peça
              </Button>
            )}


            {isMecanico && (
              <Button
                className="gap-2"
                data-testid="request-part-btn"
                onClick={() =>
                  setRequestOpen(true)
                }
              >
                <Plus className="w-4 h-4" />

                Solicitar Peça
              </Button>
            )}

          </div>
        }
      />


      {/* ============================
          CADASTRO / EDIÇÃO
          ADMIN + GESTOR
      ============================ */}

      <Dialog
        open={open}
        onOpenChange={(v) => {
          setOpen(v);

          if (!v) {
            setEditing(null);
            setForm(EMPTY);
          }
        }}
      >
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">

          <DialogHeader>
            <DialogTitle>
              {editing
                ? "Editar Peça"
                : "Nova Peça"}
            </DialogTitle>
          </DialogHeader>


          <div className="grid grid-cols-2 gap-3">

            <div>
              <Label>
                Código*
              </Label>

              <Input
                value={
                  form.part_number
                }
                onChange={(e) =>
                  setForm({
                    ...form,
                    part_number:
                      e.target.value,
                  })
                }
              />
            </div>


            <div>
              <Label>
                Nome*
              </Label>

              <Input
                value={
                  form.name
                }
                onChange={(e) =>
                  setForm({
                    ...form,
                    name:
                      e.target.value,
                  })
                }
              />
            </div>


            <div>
              <Label>
                Custo unitário (R$)
              </Label>

              <Input
                type="number"
                step="0.01"

                value={
                  form.unit_cost
                }

                onChange={(e) =>
                  setForm({
                    ...form,
                    unit_cost:
                      e.target.value,
                  })
                }
              />
            </div>


            <div>
              <Label>
                Unidade de Medida
              </Label>

              <Select
                value={
                  form.unidade_medida
                }

                onValueChange={(v) =>
                  setForm({
                    ...form,
                    unidade_medida: v,
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>

                <SelectContent>
                  {UNITS.map((u) => (
                    <SelectItem
                      key={u}
                      value={u}
                    >
                      {u}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>


            <div>
              <Label>
                Estoque Atual
              </Label>

              <Input
                type="number"
                step="0.01"

                value={
                  form.quantidade_estoque
                }

                onChange={(e) =>
                  setForm({
                    ...form,

                    quantidade_estoque:
                      e.target.value,
                  })
                }
              />
            </div>


            <div>
              <Label>
                Estoque Mínimo
              </Label>

              <Input
                type="number"

                value={
                  form.estoque_minimo
                }

                onChange={(e) =>
                  setForm({
                    ...form,

                    estoque_minimo:
                      e.target.value,
                  })
                }
              />
            </div>


            <div>
              <Label>
                Estoque Máximo
              </Label>

              <Input
                type="number"

                value={
                  form.estoque_maximo
                }

                onChange={(e) =>
                  setForm({
                    ...form,

                    estoque_maximo:
                      e.target.value,
                  })
                }
              />
            </div>


            <div>
              <Label>
                Categoria
              </Label>

              <Input
                value={
                  form.categoria
                }

                onChange={(e) =>
                  setForm({
                    ...form,

                    categoria:
                      e.target.value,
                  })
                }
              />
            </div>

          </div>


          <DialogFooter>
            <Button
              onClick={save}
            >
              Salvar
            </Button>
          </DialogFooter>

        </DialogContent>
      </Dialog>


      {/* ============================
          SOLICITAÇÃO DO MECÂNICO
      ============================ */}

      <Dialog
        open={requestOpen}
        onOpenChange={(v) => {
          setRequestOpen(v);

          if (!v) {
            setRequestForm(
              EMPTY_REQUEST
            );
          }
        }}
      >
        <DialogContent className="sm:max-w-xl">

          <DialogHeader>
            <DialogTitle>
              Solicitar Peça
            </DialogTitle>
          </DialogHeader>


          <div className="grid grid-cols-2 gap-3">

            <div>
              <Label>
                Código
              </Label>

              <Input
                placeholder="Opcional"

                value={
                  requestForm.part_number
                }

                onChange={(e) =>
                  setRequestForm({
                    ...requestForm,

                    part_number:
                      e.target.value,
                  })
                }
              />
            </div>


            <div>
              <Label>
                Nome*
              </Label>

              <Input
                value={
                  requestForm.name
                }

                onChange={(e) =>
                  setRequestForm({
                    ...requestForm,

                    name:
                      e.target.value,
                  })
                }
              />
            </div>


            <div>
              <Label>
                Quantidade necessária*
              </Label>

              <Input
                type="number"
                min="1"

                value={
                  requestForm.quantidade_necessaria
                }

                onChange={(e) =>
                  setRequestForm({
                    ...requestForm,

                    quantidade_necessaria:
                      e.target.value,
                  })
                }
              />
            </div>


            <div>
              <Label>
                Unidade de Medida
              </Label>

              <Select
                value={
                  requestForm.unidade_medida
                }

                onValueChange={(v) =>
                  setRequestForm({
                    ...requestForm,

                    unidade_medida: v,
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>

                <SelectContent>
                  {UNITS.map((u) => (
                    <SelectItem
                      key={u}
                      value={u}
                    >
                      {u}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>


            <div className="col-span-2">
              <Label>
                Categoria
              </Label>

              <Input
                placeholder="Freios, Filtros, Suspensão..."

                value={
                  requestForm.categoria
                }

                onChange={(e) =>
                  setRequestForm({
                    ...requestForm,

                    categoria:
                      e.target.value,
                  })
                }
              />
            </div>


            <div className="col-span-2">
              <Label>
                Observação
              </Label>

              <Textarea
                rows={3}

                placeholder="Motivo da solicitação, aplicação da peça..."

                value={
                  requestForm.observacao
                }

                onChange={(e) =>
                  setRequestForm({
                    ...requestForm,

                    observacao:
                      e.target.value,
                  })
                }
              />
            </div>

          </div>


          <DialogFooter>
            <Button
              onClick={requestPart}
            >
              Enviar para aprovação
            </Button>
          </DialogFooter>

        </DialogContent>
      </Dialog>


      {/* ============================
          COTAÇÃO
      ============================ */}

      <Dialog
        open={quoteOpen}
        onOpenChange={(v) => {
          setQuoteOpen(v);

          if (!v) {
            setQuotePart(null);
            setQuoteForm(
              EMPTY_QUOTE
            );
          }
        }}
      >
        <DialogContent className="sm:max-w-lg">

          <DialogHeader>
            <DialogTitle>
              Adicionar Cotação
            </DialogTitle>
          </DialogHeader>


          {quotePart && (
            <div className="text-sm text-muted-foreground">
              Peça:{" "}
              <span className="font-semibold text-foreground">
                {quotePart.name}
              </span>
            </div>
          )}


          <div className="space-y-3">

            <div>
              <Label>
                Fornecedor*
              </Label>

              <Input
                value={
                  quoteForm.supplier_name
                }

                onChange={(e) =>
                  setQuoteForm({
                    ...quoteForm,

                    supplier_name:
                      e.target.value,
                  })
                }
              />
            </div>


            <div>
              <Label>
                Quantidade
              </Label>

              <Input
                type="number"
                min="1"

                value={
                  quoteForm.quantity
                }

                onChange={(e) =>
                  setQuoteForm({
                    ...quoteForm,

                    quantity:
                      e.target.value,
                  })
                }
              />
            </div>


            <div>
              <Label>
                Valor Unitário
              </Label>

              <Input
                type="number"
                step="0.01"

                value={
                  quoteForm.unit_cost
                }

                onChange={(e) =>
                  setQuoteForm({
                    ...quoteForm,

                    unit_cost:
                      e.target.value,
                  })
                }
              />
            </div>


            <div className="rounded-md bg-secondary p-3">
              <div className="text-xs text-muted-foreground">
                Total da cotação
              </div>

              <div className="font-display font-bold text-xl">
                {brl(
                  Number(
                    quoteForm.quantity || 0
                  ) *
                  Number(
                    quoteForm.unit_cost || 0
                  )
                )}
              </div>
            </div>

          </div>


          <DialogFooter>
            <Button
              onClick={saveQuote}
            >
              Salvar Cotação
            </Button>
          </DialogFooter>

        </DialogContent>
      </Dialog>


      {/* ============================
          APROVAÇÃO
      ============================ */}

      <Dialog
        open={approvalOpen}
        onOpenChange={(v) => {
          setApprovalOpen(v);

          if (!v) {
            setApprovalPart(null);

            setApprovalForm(
              EMPTY_APPROVAL
            );
          }
        }}
      >
        <DialogContent className="sm:max-w-xl">

          <DialogHeader>
            <DialogTitle>
              Aprovar Peça
            </DialogTitle>
          </DialogHeader>


          {approvalPart && (
            <Card className="p-3 bg-secondary/50">
              <div className="font-semibold">
                {approvalPart.name}
              </div>

              <div className="text-xs text-muted-foreground">
                Solicitada por{" "}
                {approvalPart.criado_por_nome ||
                  "Mecânico"}
              </div>
            </Card>
          )}


          <div className="grid grid-cols-2 gap-3">

            <div>
              <Label>
                Custo Oficial
              </Label>

              <Input
                type="number"
                step="0.01"

                value={
                  approvalForm.unit_cost
                }

                onChange={(e) =>
                  setApprovalForm({
                    ...approvalForm,

                    unit_cost:
                      e.target.value,
                  })
                }
              />
            </div>


            <div>
              <Label>
                Estoque Inicial
              </Label>

              <Input
                type="number"

                value={
                  approvalForm.quantidade_estoque
                }

                onChange={(e) =>
                  setApprovalForm({
                    ...approvalForm,

                    quantidade_estoque:
                      e.target.value,
                  })
                }
              />
            </div>


            <div>
              <Label>
                Estoque Mínimo
              </Label>

              <Input
                type="number"

                value={
                  approvalForm.estoque_minimo
                }

                onChange={(e) =>
                  setApprovalForm({
                    ...approvalForm,

                    estoque_minimo:
                      e.target.value,
                  })
                }
              />
            </div>


            <div>
              <Label>
                Estoque Máximo
              </Label>

              <Input
                type="number"

                value={
                  approvalForm.estoque_maximo
                }

                onChange={(e) =>
                  setApprovalForm({
                    ...approvalForm,

                    estoque_maximo:
                      e.target.value,
                  })
                }
              />
            </div>

          </div>


          <DialogFooter>
            <Button
              onClick={approvePart}
            >
              <Check className="w-4 h-4 mr-2" />

              Aprovar Peça
            </Button>
          </DialogFooter>

        </DialogContent>
      </Dialog>


      {/* ============================
          REJEIÇÃO
      ============================ */}

      <Dialog
        open={rejectOpen}
        onOpenChange={(v) => {
          setRejectOpen(v);

          if (!v) {
            setRejectPart(null);
            setRejectReason("");
          }
        }}
      >
        <DialogContent className="sm:max-w-lg">

          <DialogHeader>
            <DialogTitle>
              Rejeitar Solicitação
            </DialogTitle>
          </DialogHeader>


          <Textarea
            rows={4}

            placeholder="Informe o motivo da rejeição..."

            value={
              rejectReason
            }

            onChange={(e) =>
              setRejectReason(
                e.target.value
              )
            }
          />


          <DialogFooter>
            <Button
              variant="destructive"
              onClick={
                rejectPartRequest
              }
            >
              <X className="w-4 h-4 mr-2" />

              Rejeitar
            </Button>
          </DialogFooter>

        </DialogContent>
      </Dialog>


      {/* ============================
          TABELA
      ============================ */}

      <Card className="p-0 overflow-x-auto">

        <Table>

          <TableHeader>
            <TableRow>

              <TableHead>
                Código
              </TableHead>

              <TableHead>
                Nome
              </TableHead>

              <TableHead>
                Categoria
              </TableHead>

              <TableHead>
                UM
              </TableHead>

              <TableHead className="text-right">
                Estoque
              </TableHead>

              <TableHead className="text-right">
                Mínimo
              </TableHead>

              <TableHead className="text-right">
                Máximo
              </TableHead>

              <TableHead className="text-right">
                Custo
              </TableHead>

              <TableHead>
                Status
              </TableHead>

              <TableHead className="text-right w-16">
                Ações
              </TableHead>

            </TableRow>
          </TableHeader>


          <TableBody>

            {list.map((p) => {

              const inactive =
                p.active === false;

              const qty =
                Number(
                  p.quantidade_estoque || 0
                );

              const min =
                Number(
                  p.estoque_minimo || 0
                );

              const max =
                Number(
                  p.estoque_maximo || 0
                );

              const lowStock =
                p.status_aprovacao !==
                  "Pendente" &&
                min > 0 &&
                qty <= min;


              return (
                <TableRow
                  key={p.id}

                  data-testid={
                    `part-row-${p.id}`
                  }

                  className={`
                    ${stockRowClass(p)}
                    ${inactive &&
                    p.status_aprovacao !==
                      "Pendente"
                      ? "opacity-60"
                      : ""}
                  `}
                >

                  <TableCell className="font-mono">
                    {p.part_number || "—"}
                  </TableCell>


                  <TableCell className="font-semibold">

                    <div className="flex items-center gap-2">

                      <Package className="w-4 h-4 text-primary" />

                      {p.name}

                      {lowStock && (
                        <AlertTriangle className="w-3.5 h-3.5 text-yellow-600" />
                      )}

                    </div>


                    {p.status_aprovacao ===
                      "Pendente" &&
                      p.criado_por_nome && (
                        <div className="text-[10px] text-muted-foreground mt-1">
                          Solicitada por{" "}
                          {p.criado_por_nome}
                        </div>
                      )}

                  </TableCell>


                  <TableCell className="text-xs text-muted-foreground">
                    {p.categoria || "—"}
                  </TableCell>


                  <TableCell className="text-xs">
                    {p.unidade_medida ||
                      "UN"}
                  </TableCell>


                  <TableCell
                    className={`text-right font-mono ${
                      lowStock
                        ? "text-destructive font-bold"
                        : ""
                    }`}
                  >
                    {p.status_aprovacao ===
                    "Pendente"
                      ? "—"
                      : qty.toLocaleString(
                          "pt-BR"
                        )}
                  </TableCell>


                  <TableCell className="text-right font-mono text-muted-foreground">
                    {p.status_aprovacao ===
                    "Pendente"
                      ? "—"
                      : min.toLocaleString(
                          "pt-BR"
                        )}
                  </TableCell>


                  <TableCell className="text-right font-mono text-muted-foreground">
                    {p.status_aprovacao ===
                    "Pendente"
                      ? "—"
                      : max.toLocaleString(
                          "pt-BR"
                        )}
                  </TableCell>


                  <TableCell className="text-right font-mono">
                    {p.status_aprovacao ===
                    "Pendente"
                      ? "—"
                      : brl(p.unit_cost)}
                  </TableCell>


                  <TableCell>
                    {renderStatus(p)}
                  </TableCell>


                  <TableCell className="text-right">

                    {(canManageParts ||
                      isMecanico) && (

                      <DropdownMenu>

                        <DropdownMenuTrigger
                          asChild
                        >
                          <Button
                            variant="ghost"
                            size="icon"
                            className="w-7 h-7"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>


                        <DropdownMenuContent
                          align="end"
                          className="w-52"
                        >

                          {isMecanico &&
                            p.status_aprovacao ===
                              "Pendente" && (

                              <DropdownMenuItem
                                onClick={() =>
                                  openQuote(p)
                                }
                              >
                                <ReceiptText className="w-4 h-4 mr-2" />

                                Adicionar Cotação
                              </DropdownMenuItem>
                            )}


                          {canManageParts &&
                            p.status_aprovacao ===
                              "Pendente" && (
                              <>
                                <DropdownMenuItem
                                  onClick={() =>
                                    openApproval(
                                      p
                                    )
                                  }
                                >
                                  <Check className="w-4 h-4 mr-2" />

                                  Aprovar
                                </DropdownMenuItem>


                                <DropdownMenuItem
                                  onClick={() =>
                                    openReject(
                                      p
                                    )
                                  }
                                  className="text-destructive focus:text-destructive"
                                >
                                  <X className="w-4 h-4 mr-2" />

                                  Rejeitar
                                </DropdownMenuItem>

                                <DropdownMenuSeparator />
                              </>
                            )}


                          {canManageParts &&
                            p.status_aprovacao !==
                              "Pendente" && (
                              <>
                                <DropdownMenuItem
                                  onClick={() =>
                                    openEdit(p)
                                  }
                                >
                                  <Pencil className="w-4 h-4 mr-2" />

                                  Editar
                                </DropdownMenuItem>


                                <DropdownMenuItem
                                  onClick={() =>
                                    toggleActive(
                                      p
                                    )
                                  }
                                >
                                  <Power className="w-4 h-4 mr-2" />

                                  {inactive
                                    ? "Ativar"
                                    : "Inativar"}
                                </DropdownMenuItem>
                              </>
                            )}


                          {canDeleteParts && (
                            <>
                              <DropdownMenuSeparator />

                              <DropdownMenuItem
                                onClick={() =>
                                  setPendingDelete(
                                    p
                                  )
                                }
                                className="text-destructive focus:text-destructive"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />

                                Excluir
                              </DropdownMenuItem>
                            </>
                          )}

                        </DropdownMenuContent>

                      </DropdownMenu>
                    )}

                  </TableCell>

                </TableRow>
              );

            })}

          </TableBody>

        </Table>

      </Card>


      {/* ============================
          EXCLUSÃO
      ============================ */}

      <AlertDialog
        open={
          !!pendingDelete
        }

        onOpenChange={(v) =>
          !v &&
          setPendingDelete(null)
        }
      >

        <AlertDialogContent>

          <AlertDialogHeader>

            <AlertDialogTitle>
              Excluir peça?
            </AlertDialogTitle>

            <AlertDialogDescription>
              Tem certeza que deseja excluir a peça{" "}
              <b>
                {pendingDelete?.name}
              </b>
              ? Esta ação não pode ser desfeita.
            </AlertDialogDescription>

          </AlertDialogHeader>


          <AlertDialogFooter>

            <AlertDialogCancel>
              Cancelar
            </AlertDialogCancel>


            <AlertDialogAction
              onClick={doDelete}

              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>

          </AlertDialogFooter>

        </AlertDialogContent>

      </AlertDialog>

    </div>
  );
}